import React, { useEffect, useMemo, useRef, useState } from "react";
import { closeIcon } from "assets/icons";
import Button from "components/Buttons/Button";
import IconButton from "components/Buttons/IconButton";
import { formatNumber } from "utils/formatNumber";
import {
  AllAction,
  AmountInput,
  FieldGroup,
  FieldHint,
  FieldLabel,
  FieldWrap,
  Input,
  ModalActions,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SubText,
} from "./WithdrawModal.styles";
import { WithdrawModalProps } from "./WithdrawModal.types";

const sanitizeAmount = (value: string) => {
  const normalized = value.replace(/\s/g, "").replace(",", ".").replace(/[^\d.]/g, "");
  const [whole = "", ...fractionParts] = normalized.split(".");
  const fraction = fractionParts.join("").slice(0, 2);
  return fractionParts.length ? `${whole}.${fraction}` : whole;
};

const formatCardNumber = (value: string) =>
  value.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();

const WithdrawModal: React.FC<WithdrawModalProps> = ({
  availableAmount,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [amount, setAmount] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const amountRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    amountRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  const amountValue = Number(sanitizeAmount(amount) || 0);
  const cardDigits = cardNumber.replace(/\D/g, "");

  const amountError = useMemo(() => {
    if (!amount) return "";
    if (!Number.isFinite(amountValue) || amountValue <= 0) return "Введите сумму больше нуля";
    if (amountValue > availableAmount) return "Больше, чем на балансе";
    return "";
  }, [amount, amountValue, availableAmount]);

  const cardError = cardNumber && cardDigits.length !== 16 ? "Нужно 16 цифр" : "";
  const canSubmit = amountValue > 0
    && amountValue <= availableAmount
    && cardDigits.length === 16
    && !amountError;

  const handleSubmit = () => {
    if (!canSubmit || isSubmitting) return;
    onSubmit({
      amount: String(amountValue),
      cardNumber: cardDigits,
    });
  };

  return (
    <ModalOverlay
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <ModalContent role="dialog" aria-modal="true" aria-labelledby="withdraw-title">
        <ModalHeader>
          <div>
            <h2 id="withdraw-title">Вывод средств</h2>
            <SubText>
              Доступно к выводу <strong>{formatNumber(availableAmount)} руб.</strong>
            </SubText>
          </div>
          <IconButton icon={closeIcon} altText="Закрыть" onClick={onCancel} />
        </ModalHeader>

        <FieldGroup>
          <FieldLabel htmlFor="withdraw-amount">Сумма</FieldLabel>
          <FieldWrap>
            <AmountInput
              ref={amountRef}
              id="withdraw-amount"
              inputMode="decimal"
              value={amount}
              $hasError={Boolean(amountError)}
              placeholder="0"
              onChange={(event) => {
                const sanitized = sanitizeAmount(event.target.value);
                setAmount(sanitized.replace(".", ","));
              }}
            />
            <AllAction>
              <Button
                variant="accent"
                size="sm"
                onClick={() => setAmount(String(availableAmount).replace(".", ","))}
                disabled={availableAmount <= 0}
              >
                Всё
              </Button>
            </AllAction>
          </FieldWrap>
          <FieldHint $error={Boolean(amountError)} role={amountError ? "alert" : undefined}>
            {amountError}
          </FieldHint>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="withdraw-card">Реквизиты</FieldLabel>
          <Input
            id="withdraw-card"
            inputMode="numeric"
            autoComplete="cc-number"
            value={cardNumber}
            $hasError={Boolean(cardError)}
            placeholder="0000 0000 0000 0000"
            onChange={(event) => setCardNumber(formatCardNumber(event.target.value))}
          />
          <FieldHint $error={Boolean(cardError)} role={cardError ? "alert" : undefined}>
            {cardError}
          </FieldHint>
        </FieldGroup>

        <ModalActions>
          <Button variant="secondary" size="md" onClick={onCancel} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            Отправить заявку
          </Button>
        </ModalActions>
      </ModalContent>
    </ModalOverlay>
  );
};

export default WithdrawModal;
